import { formatAddressString } from '@/utils/address';
import { usePrevious } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { useLayoutEffect, useMemo } from 'react';
import { create, useStore } from 'zustand';

type CacheKeyPart = string | number;

type DeliveryOptionsState = {
  cacheKey: CacheKeyPart[] | undefined;
  setCacheKey: (newCacheKey: CacheKeyPart[]) => void;
};

const customerPortalClient = getTsCustomerPortalClient();

const buildDeliveryOptionsCacheKey = (products: Array<{ productId: number; quantity: number }>, address?: Address) => {
  const productsKey = products.map((p) => `${p.productId}:${p.quantity}`);
  const addressKey = [address ? formatAddressString(address) : undefined].filter(Boolean) as string[];

  return [...customerPortalClient.listCartDeliveryOptionsAndReserveInventory.keys, ...productsKey, ...addressKey];
};

export const deliveryOptionsCacheKeyStore = create<DeliveryOptionsState>((set) => ({
  cacheKey: undefined,
  setCacheKey: (cacheKey) => set(() => ({ cacheKey })),
}));

export const useDeliveryOptionsCacheKeyStore = ({
  products,
  address,
}: {
  products: {
    productId: number;
    quantity: number;
  }[];
  address: Address;
}) => {
  const { cacheKey, setCacheKey } = useStore(deliveryOptionsCacheKeyStore);

  const newCacheKey = useMemo(() => {
    return buildDeliveryOptionsCacheKey(products, address);
  }, [address, products]);

  useLayoutEffect(() => {
    const isChanged =
      cacheKey?.length !== newCacheKey.length || cacheKey.some((value, index) => value !== newCacheKey[index]);

    if (isChanged) {
      setCacheKey(newCacheKey);
    }
  }, [cacheKey, newCacheKey, setCacheKey]);

  const previousCacheKey = usePrevious(cacheKey);

  return { cacheKey, previousCacheKey, setCacheKey };
};
